import { useState, useMemo } from 'react'
import { X, BarChart2, Hash, Clock, Star, Folder } from 'lucide-react'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'

interface BookmarkAnalyticsProps {
  onClose: () => void
}

interface TagStats {
  tag: string
  count: number
  percentage: number
}

interface TimeStats {
  period: string
  count: number
}

interface CategoryStats {
  name: string
  count: number
  percentage: number
}

export function BookmarkAnalytics({ onClose }: BookmarkAnalyticsProps) {
  const { bookmarks, categories } = useBookmarkStore()
  const [activeTab, setActiveTab] = useState<'overview' | 'tags' | 'time' | 'categories'>('overview')

  // 计算总体统计信息
  const overviewStats = useMemo(() => {
    const favoriteCount = bookmarks.filter(b => b.isFavorite).length
    const withTagsCount = bookmarks.filter(b => b.tags.length > 0).length
    const withCategoryCount = bookmarks.filter(b => b.categoryId).length
    const withDescriptionCount = bookmarks.filter(b => b.description).length

    return {
      total: bookmarks.length,
      favorites: favoriteCount,
      withTags: withTagsCount,
      withCategory: withCategoryCount,
      withDescription: withDescriptionCount,
    }
  }, [bookmarks])

  // 计算标签统计信息
  const tagStats = useMemo(() => {
    const tagCounts = new Map<string, number>()
    bookmarks.forEach(bookmark => {
      bookmark.tags.forEach(tag => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1)
      })
    })

    const stats: TagStats[] = Array.from(tagCounts.entries()).map(([tag, count]) => ({
      tag,
      count,
      percentage: (count / bookmarks.length) * 100,
    }))

    return stats.sort((a, b) => b.count - a.count)
  }, [bookmarks])

  // 计算时间统计信息
  const timeStats = useMemo(() => {
    const now = new Date()
    const periods: { name: string; start: number }[] = [
      { name: '今天', start: now.setHours(0, 0, 0, 0) },
      { name: '本周', start: now.setDate(now.getDate() - now.getDay()) },
      { name: '本月', start: now.setDate(1) },
      { name: '今年', start: new Date(now.getFullYear(), 0, 1).getTime() },
    ]

    return periods.map(period => ({
      period: period.name,
      count: bookmarks.filter(b => b.createdAt >= period.start).length,
    }))
  }, [bookmarks])

  // 计算分类统计信息
  const categoryStats = useMemo(() => {
    const categoryCounts = new Map<string, number>()
    bookmarks.forEach(bookmark => {
      const categoryName = categories.find(c => c.id === bookmark.categoryId)?.name || '未分类'
      categoryCounts.set(categoryName, (categoryCounts.get(categoryName) || 0) + 1)
    })

    return Array.from(categoryCounts.entries())
      .map(([name, count]) => ({
        name,
        count,
        percentage: (count / bookmarks.length) * 100,
      }))
      .sort((a, b) => b.count - a.count)
  }, [bookmarks, categories])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-medium">书签统计分析</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b">
          <Button
            variant={activeTab === 'overview' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('overview')}
            className="flex items-center rounded-none"
          >
            <BarChart2 className="w-4 h-4 mr-2" />
            总览
          </Button>
          <Button
            variant={activeTab === 'tags' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('tags')}
            className="flex items-center rounded-none"
          >
            <Hash className="w-4 h-4 mr-2" />
            标签分析
          </Button>
          <Button
            variant={activeTab === 'time' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('time')}
            className="flex items-center rounded-none"
          >
            <Clock className="w-4 h-4 mr-2" />
            时间分析
          </Button>
          <Button
            variant={activeTab === 'categories' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('categories')}
            className="flex items-center rounded-none"
          >
            <Folder className="w-4 h-4 mr-2" />
            分类分析
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-blue-800 mb-4">基本统计</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-blue-600">总书签数</span>
                    <span className="font-medium">{overviewStats.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">收藏书签</span>
                    <span className="font-medium">{overviewStats.favorites}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">带标签书签</span>
                    <span className="font-medium">{overviewStats.withTags}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">已分类书签</span>
                    <span className="font-medium">{overviewStats.withCategory}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-600">带描述书签</span>
                    <span className="font-medium">{overviewStats.withDescription}</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-green-800 mb-4">使用率统计</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-green-600">收藏率</span>
                    <div className="flex items-center">
                      <div className="w-32 h-2 bg-green-200 rounded-full mr-2">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(overviewStats.favorites / overviewStats.total) * 100}%` }}
                        />
                      </div>
                      <span className="font-medium">
                        {((overviewStats.favorites / overviewStats.total) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-600">标签率</span>
                    <div className="flex items-center">
                      <div className="w-32 h-2 bg-green-200 rounded-full mr-2">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(overviewStats.withTags / overviewStats.total) * 100}%` }}
                        />
                      </div>
                      <span className="font-medium">
                        {((overviewStats.withTags / overviewStats.total) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-600">分类率</span>
                    <div className="flex items-center">
                      <div className="w-32 h-2 bg-green-200 rounded-full mr-2">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(overviewStats.withCategory / overviewStats.total) * 100}%` }}
                        />
                      </div>
                      <span className="font-medium">
                        {((overviewStats.withCategory / overviewStats.total) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">标签使用情况</h3>
              <div className="flex flex-wrap gap-2">
                {tagStats.map(({ tag, count, percentage }) => (
                  <div
                    key={tag}
                    className="px-3 py-2 bg-blue-50 rounded-lg flex items-center"
                    style={{
                      fontSize: `${Math.max(0.8, Math.min(1.5, 0.8 + percentage / 50))}rem`,
                    }}
                  >
                    <span className="text-blue-800">{tag}</span>
                    <span className="ml-2 text-blue-600 text-sm">({count})</span>
                  </div>
                ))}
              </div>
              {tagStats.length === 0 && (
                <p className="text-gray-500 text-center">暂无标签数据</p>
              )}
            </div>
          )}

          {activeTab === 'time' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">时间分布</h3>
              <div className="space-y-4">
                {timeStats.map(({ period, count }) => (
                  <div key={period} className="flex items-center">
                    <span className="w-20 text-gray-600">{period}</span>
                    <div className="flex-1 h-6 bg-purple-100 rounded-full ml-4">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${(count / bookmarks.length) * 100}%` }}
                      />
                    </div>
                    <span className="ml-4 w-16 text-right font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium">分类分布</h3>
              <div className="space-y-4">
                {categoryStats.map(({ name, count, percentage }) => (
                  <div key={name} className="flex items-center">
                    <span className="w-32 truncate text-gray-600">{name}</span>
                    <div className="flex-1 h-6 bg-indigo-100 rounded-full ml-4">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="ml-4 w-20 text-right">
                      <span className="font-medium">{count}</span>
                      <span className="text-gray-500 ml-1">({percentage.toFixed(1)}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 